import { http } from '@/lib/http'

export interface Conversation {
  id: string
  workspaceId: string
  title: string
  titleSource?: 'system' | 'auto' | 'user'
  titleLocked?: boolean
  titleGeneratedAt?: string
  icon?: string
  meta?: Record<string, unknown>
  position: number
  createdBy?: string
  updatedBy?: string
  createdAt: string
  updatedAt: string
  archivedAt?: string
  version: number
}

export interface ConversationSession {
  id: string
  conversationId: string
  userId: string
  title?: string
  summary?: string
  messageCount: number
  tokenCount: number
  status: 'active' | 'archived' | 'summarized'
  createdAt: string
  updatedAt: string
  lastMessageAt?: string
}

export interface ConversationMessage {
  id: string
  sessionId: string
  role: 'user' | 'assistant' | 'system' | 'tool'
  content: string
  toolCalls?: any[]
  toolResults?: any[]
  tokenCount: number
  isSummarized: boolean
  createdAt: string
  mode?: 'local' | 'api'
  durationMs?: number
  routing?: string
  thinking?: string
  toolsUsed?: number
}

export interface WorkspaceModule {
  id: string
  workspaceId: string
  conversationId?: string
  type: string
  label: string
  position: number
  size: string
  config: Record<string, unknown>
  parentId?: string
  createdBy?: string
  updatedBy?: string
  createdAt: string
  updatedAt: string
  archivedAt?: string
  version: number
  // Primary-rail signals (issue #3235). Serialized by the API in camelCase via
  // mapModuleEntity (apps/api .../workspaces.router.ts) — the wire field is
  // `isPrimaryRail`, NOT `is_primary_rail`. Optional because older clients/rows
  // may omit it; the API defaults it to `false`.
  isPrimaryRail?: boolean
  primaryRailOrder?: number
}

export interface ModuleSidebarPreference {
  id: string
  workspaceId: string
  userId: string
  moduleId: string
  pinned: boolean
  pinOrder: number | null
  lastOpenedAt: string | null
  displayNameOverride: string | null
  settings: { starredMetrics?: string[]; hiddenMetrics?: string[] } | null
  moduleLabel?: string
  moduleType?: string
  moduleArchivedAt?: string
  createdAt: string
  updatedAt: string
  version: number
}

export interface ConversationPanel {
  id: string
  workspaceId: string
  conversationId: string
  moduleId: string
  panelKey: string
  title?: string
  state: Record<string, unknown>
  isPinned: boolean
  isCollapsed: boolean
  sortOrder: number
  createdBy?: string
  updatedBy?: string
  createdAt: string
  updatedAt: string
  archivedAt?: string
  version: number
}

export interface ConversationLink {
  id: string
  conversationId: string
  objectId: string
  objectType: string
  role: string
  meta?: Record<string, unknown>
  createdBy?: string
  createdAt: string
  deletedAt?: string
}

export interface CreateConversationRequest {
  title?: string
  icon?: string
  meta?: Record<string, unknown>
}

export interface UpdateConversationRequest {
  title?: string
  archived?: boolean
  icon?: string | null
  meta?: Record<string, unknown>
}

export async function listConversations(
  authToken: string | null,
  workspaceId: string,
  options?: { limit?: number; includeArchived?: boolean }
): Promise<Conversation[]> {
  const searchParams: Record<string, string> = {}
  if (options?.limit) searchParams.limit = String(options.limit)
  if (options?.includeArchived) searchParams.includeArchived = 'true'

  const response = await http.get<{ items: Conversation[] }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations`,
    { authToken, searchParams }
  )

  return response.data.items
}

export async function createConversation(
  authToken: string | null,
  workspaceId: string,
  data?: CreateConversationRequest
): Promise<Conversation> {
  const response = await http.post<Conversation>(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations`,
    data ?? {},
    { authToken }
  )

  return response.data
}

export async function getConversation(
  authToken: string | null,
  workspaceId: string,
  conversationId: string
): Promise<Conversation> {
  const response = await http.get<Conversation>(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}`,
    { authToken }
  )

  return response.data
}

/**
 * Server-assembled ConversationJson snapshot (#1282). Returns the
 * `conversation` + `objects` + `summary` triple; the caller (useConversationJson)
 * wraps it with the web-only `schemaHash` / `fetchedAt` fields and merges global
 * memories. Typed loosely here because the canonical shape lives in
 * `@/lib/query-engine/types`; the hook narrows it.
 */
export async function getConversationJson(
  authToken: string | null,
  workspaceId: string,
  conversationId: string
): Promise<{
  conversation: {
    id: string
    name: string
    modules: Array<{ id: string; type: string; label: string; objectType?: string }>
  }
  objects: Record<string, any[]>
  summary: {
    totalObjects: number
    byType: Record<string, number>
    truncatedTypes: string[]
    moduleCount: number
  }
}> {
  const response = await http.get<{
    conversation: {
      id: string
      name: string
      modules: Array<{ id: string; type: string; label: string; objectType?: string }>
    }
    objects: Record<string, any[]>
    summary: {
      totalObjects: number
      byType: Record<string, number>
      truncatedTypes: string[]
      moduleCount: number
    }
  }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}/json`,
    { authToken }
  )

  return response.data
}

export async function updateConversation(
  authToken: string | null,
  workspaceId: string,
  conversationId: string,
  patch: UpdateConversationRequest
): Promise<Conversation> {
  const response = await http.patch<Conversation>(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}`,
    patch,
    { authToken }
  )

  return response.data
}

export async function deleteConversation(
  authToken: string | null,
  workspaceId: string,
  conversationId: string,
  options?: { permanent?: boolean }
): Promise<void> {
  await http.delete(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}`,
    {
      authToken,
      ...(options?.permanent ? { searchParams: { permanent: 'true' } } : {}),
    }
  )
}

export async function reorderConversations(
  authToken: string | null,
  workspaceId: string,
  conversationIds: string[]
): Promise<Conversation[]> {
  const response = await http.patch<{ items: Conversation[] }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/reorder`,
    { conversationIds },
    { authToken }
  )

  return response.data.items
}

export async function getConversationSession(
  authToken: string | null,
  workspaceId: string,
  conversationId: string
): Promise<ConversationSession> {
  const response = await http.get<ConversationSession>(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}/session`,
    { authToken }
  )

  return response.data
}

export async function getConversationMessages(
  authToken: string | null,
  workspaceId: string,
  conversationId: string,
  options?: { sessionId?: string; limit?: number; includeSummarized?: boolean }
): Promise<{ messages: ConversationMessage[]; session: ConversationSession | null }> {
  const searchParams: Record<string, string> = {}
  if (options?.sessionId) searchParams.sessionId = options.sessionId
  if (options?.limit) searchParams.limit = String(options.limit)
  if (options?.includeSummarized) searchParams.includeSummarized = 'true'

  const response = await http.get<{ messages: ConversationMessage[]; session: ConversationSession | null }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}/messages`,
    { authToken, searchParams }
  )

  return response.data
}

export async function saveConversationMessage(
  authToken: string | null,
  workspaceId: string,
  conversationId: string,
  message: {
    role: 'user' | 'assistant' | 'system' | 'tool'
    content: string
    mode?: 'local' | 'api'
    durationMs?: number
    routing?: string
    thinking?: string
    toolsUsed?: number
    toolCalls?: any[]
    toolResults?: any[]
  }
): Promise<ConversationMessage> {
  const response = await http.post<ConversationMessage>(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}/messages`,
    message,
    { authToken }
  )

  return response.data
}

export async function listWorkspaceModules(
  authToken: string | null,
  workspaceId: string,
  options?: { includeArchived?: boolean; type?: string }
): Promise<WorkspaceModule[]> {
  const searchParams: Record<string, string> = {}
  if (options?.includeArchived) searchParams.includeArchived = 'true'
  if (options?.type) searchParams.type = options.type

  const response = await http.get<{ items: WorkspaceModule[] }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/modules`,
    { authToken, searchParams }
  )

  return response.data.items
}

export async function listModuleSidebarPreferences(
  authToken: string | null,
  workspaceId: string,
  options?: { includeArchived?: boolean }
): Promise<ModuleSidebarPreference[]> {
  const searchParams: Record<string, string> = {}
  if (options?.includeArchived) searchParams.includeArchived = 'true'

  const response = await http.get<{ items: ModuleSidebarPreference[] }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/module-preferences`,
    { authToken, searchParams }
  )

  return response.data.items
}

export async function updateModuleSidebarPreference(
  authToken: string | null,
  workspaceId: string,
  moduleId: string,
  patch: {
    pinned?: boolean
    pinOrder?: number | null
    lastOpenedAt?: string | null
    displayNameOverride?: string | null
    settings?: { starredMetrics?: string[]; hiddenMetrics?: string[] } | null
  }
): Promise<ModuleSidebarPreference> {
  const response = await http.patch<ModuleSidebarPreference>(
    `/workspaces/${encodeURIComponent(workspaceId)}/module-preferences/${encodeURIComponent(moduleId)}`,
    patch,
    { authToken }
  )

  return response.data
}

export async function createWorkspaceModule(
  authToken: string | null,
  workspaceId: string,
  payload: {
    id?: string
    type: string
    label?: string
    position?: number
    size?: string
    config?: Record<string, unknown>
    parentId?: string
  },
  options?: { idempotencyKey?: string }
): Promise<WorkspaceModule> {
  const headers: Record<string, string> = {}
  if (options?.idempotencyKey) {
    headers['Idempotency-Key'] = options.idempotencyKey
  }
  const response = await http.post<WorkspaceModule>(
    `/workspaces/${encodeURIComponent(workspaceId)}/modules`,
    payload,
    { authToken, headers }
  )

  return response.data
}

export async function updateWorkspaceModule(
  authToken: string | null,
  workspaceId: string,
  moduleId: string,
  patch: {
    label?: string
    position?: number
    size?: string
    config?: Record<string, unknown>
    archived?: boolean
    parentId?: string | null
  }
): Promise<WorkspaceModule> {
  const response = await http.patch<WorkspaceModule>(
    `/workspaces/${encodeURIComponent(workspaceId)}/modules/${encodeURIComponent(moduleId)}`,
    patch,
    { authToken }
  )

  return response.data
}

export async function deleteWorkspaceModule(
  authToken: string | null,
  workspaceId: string,
  moduleId: string,
  options?: { permanent?: boolean }
): Promise<void> {
  const searchParams: Record<string, string> = {}
  if (options?.permanent) searchParams.permanent = 'true'

  await http.delete(
    `/workspaces/${encodeURIComponent(workspaceId)}/modules/${encodeURIComponent(moduleId)}`,
    { authToken, searchParams }
  )
}

export async function restoreWorkspaceModule(
  authToken: string | null,
  workspaceId: string,
  moduleId: string
): Promise<void> {
  await http.post(
    `/workspaces/${encodeURIComponent(workspaceId)}/modules/${encodeURIComponent(moduleId)}/restore`,
    {},
    { authToken }
  )
}

export async function publishReport(
  authToken: string | null,
  workspaceId: string,
  moduleId: string,
): Promise<{ slug: string; url: string }> {
  const response = await http.post<{ slug: string; url: string }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/reports/${encodeURIComponent(moduleId)}/publish`,
    {},
    { authToken }
  )
  return response.data
}

export async function unpublishReport(
  authToken: string | null,
  workspaceId: string,
  moduleId: string,
): Promise<void> {
  await http.delete(
    `/workspaces/${encodeURIComponent(workspaceId)}/reports/${encodeURIComponent(moduleId)}/publish`,
    { authToken }
  )
}

export async function fetchMySlug(
  authToken: string | null,
): Promise<{ slug: string; isCustom: boolean; claimedAt: string } | null> {
  try {
    const response = await http.get<{ slug: string; isCustom: boolean; claimedAt: string }>(
      '/pub/me/slug',
      { authToken },
    )
    return response.data
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 404) {
      return null
    }
    throw err
  }
}

export async function listConversationPanels(
  authToken: string | null,
  workspaceId: string,
  conversationId: string,
  options?: { includeArchived?: boolean }
): Promise<ConversationPanel[]> {
  const searchParams: Record<string, string> = {}
  if (options?.includeArchived) searchParams.includeArchived = 'true'

  const response = await http.get<{ items: ConversationPanel[] }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}/panels`,
    { authToken, searchParams }
  )

  return response.data.items
}

export async function createConversationPanel(
  authToken: string | null,
  workspaceId: string,
  conversationId: string,
  payload: {
    workspaceModuleId: string
    panelKey: string
    title?: string
    initialState?: Record<string, unknown>
    isPinned?: boolean
    isCollapsed?: boolean
    sortOrder?: number
  }
): Promise<ConversationPanel> {
  const response = await http.post<ConversationPanel>(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}/panels`,
    payload,
    { authToken }
  )

  return response.data
}

export async function updateConversationPanel(
  authToken: string | null,
  workspaceId: string,
  conversationId: string,
  panelId: string,
  patch: {
    title?: string | null
    state?: Record<string, unknown>
    isPinned?: boolean
    isCollapsed?: boolean
    sortOrder?: number
    archived?: boolean
  }
): Promise<ConversationPanel> {
  const response = await http.patch<ConversationPanel>(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}/panels/${encodeURIComponent(panelId)}`,
    patch,
    { authToken }
  )

  return response.data
}

export async function deleteConversationPanel(
  authToken: string | null,
  workspaceId: string,
  conversationId: string,
  panelId: string
): Promise<void> {
  await http.delete(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}/panels/${encodeURIComponent(panelId)}`,
    { authToken }
  )
}

export async function listConversationLinks(
  authToken: string | null,
  workspaceId: string,
  conversationId: string,
  options?: {
    objectType?: string
    moduleId?: string
    label?: string
    limit?: number
    offset?: number
  }
): Promise<ConversationLink[]> {
  const searchParams: Record<string, string> = {}
  if (options?.objectType) searchParams.object_type = options.objectType
  if (options?.moduleId) searchParams.module_id = options.moduleId
  if (options?.label) searchParams.label = options.label
  if (options?.limit !== undefined) searchParams.limit = String(options.limit)
  if (options?.offset !== undefined) searchParams.offset = String(options.offset)

  const response = await http.get<{ items: ConversationLink[] }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}/links`,
    { authToken, searchParams }
  )

  return response.data.items
}

export async function createConversationLink(
  authToken: string | null,
  workspaceId: string,
  conversationId: string,
  payload: {
    objectId: string
    objectType: string
    role?: string
    meta?: Record<string, unknown>
  }
): Promise<ConversationLink> {
  const response = await http.post<ConversationLink>(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}/links`,
    payload,
    { authToken }
  )

  return response.data
}

export async function deleteConversationLink(
  authToken: string | null,
  workspaceId: string,
  conversationId: string,
  objectId: string
): Promise<void> {
  await http.delete(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}/links/${encodeURIComponent(objectId)}`,
    { authToken }
  )
}

/**
 * Link an existing object (e.g. a contact) to a module — used to add a contact
 * to a Clients module. Idempotent server-side (returns alreadyLinked if present).
 */
export async function linkObjectToModule(
  authToken: string | null,
  workspaceId: string,
  moduleId: string,
  objectId: string,
  objectType: string
): Promise<{ id: string; moduleId: string; objectId: string; objectType: string; alreadyLinked?: boolean }> {
  const response = await http.post<{ id: string; moduleId: string; objectId: string; objectType: string; alreadyLinked?: boolean }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/modules/${encodeURIComponent(moduleId)}/links`,
    { objectId, objectType },
    { authToken }
  )

  return response.data
}

export async function unlinkObjectFromModule(
  authToken: string | null,
  workspaceId: string,
  moduleId: string,
  objectId: string
): Promise<void> {
  await http.delete(
    `/workspaces/${encodeURIComponent(workspaceId)}/modules/${encodeURIComponent(moduleId)}/links/${encodeURIComponent(objectId)}`,
    { authToken }
  )
}
